//src/pages/cadastros/UsersPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { CrudRowActions } from "@/components/shared/CrudRowActions";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { useAuth } from "@/contexts/AuthContext";
import { useSetUserActive, useUsers } from "@/hooks/useUsers";
import { maskCNPJ, maskCPF, onlyDigits } from "@/lib/masks";
import { PERMISSIONS } from "@/lib/permissions";
import type { UserListItem } from "@/services/users";

type StatusFilter = "active" | "inactive" | "all";

const LIMIT = 50;

function normalizeText(value: string) {
  return (value || "").toLowerCase().trim();
}

function formatDocumentByType(
  documentNumber?: string | null,
  personType?: "PF" | "PJ" | string | null,
) {
  const digits = onlyDigits(documentNumber || "");
  if (!digits) return "-";
  if (personType === "PJ") return maskCNPJ(digits);
  if (personType === "PF") return maskCPF(digits);
  return digits.length <= 11 ? maskCPF(digits) : maskCNPJ(digits);
}


function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export default function UsersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isClientContext = user?.context_type === "client";
  const readPermission = isClientContext
    ? PERMISSIONS.USERS_READ
    : PERMISSIONS.TENANT_USERS_READ;
  const writePermission = isClientContext
    ? PERMISSIONS.USERS_WRITE
    : PERMISSIONS.TENANT_USERS_WRITE;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [offset, setOffset] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState<UserListItem | null>(null);

  const canWriteUsers = user?.permissions?.includes(writePermission) ?? false;

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, statusFilter]);

  const params = useMemo(
    () => ({ q: debouncedSearch, limit: LIMIT, offset }),
    [debouncedSearch, offset],
  );

  const usersQuery = useUsers(params);
  const setActiveMutation = useSetUserActive();
  const allUsers = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);

  const filteredUsers = useMemo(() => {
    const query = normalizeText(debouncedSearch);
    let users = allUsers;

    if (statusFilter === "active") users = users.filter((item) => item.is_active);
    if (statusFilter === "inactive") users = users.filter((item) => !item.is_active);
    if (!query) return users;

    return users.filter((item) =>
      [
        item.name,
        item.email,
        item.document_number,
        item.unit_name,
        item.employee_code,
        item.job_position_name,
        item.department_name,
        item.person_type,
      ].some((value) => normalizeText(value ?? "").includes(query)),
    );
  }, [allUsers, debouncedSearch, statusFilter]);

  const hasNextPage = allUsers.length === LIMIT;
  const canGoBack = offset > 0;
  const isBusy = usersQuery.isFetching || setActiveMutation.isPending;

  function openStatusConfirmation(item: UserListItem) {
    if (!canWriteUsers) return;
    setSelected(item);
    setConfirmOpen(true);
  }

  async function confirmStatusChange() {
    if (!selected || !canWriteUsers) return;
    const nextStatus = !selected.is_active;

    try {
      await setActiveMutation.mutateAsync({ id: selected.id, is_active: nextStatus });
      toast.success(nextStatus ? "Usuário ativado" : "Usuário desativado");
      setConfirmOpen(false);
      setSelected(null);
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Erro ao alterar status"));
    }
  }

  return (
    <div>
      <PageHeader
        title="Usuários"
        description={
          isClientContext
            ? "Gerencie os usuários e perfis de acesso deste Cliente."
            : "Gerencie acessos ao Portal da Empresa e aos Clientes da carteira."
        }
      >
        <PermissionButton
          permission={writePermission}
          onClick={() => navigate("/cadastros/usuarios/novo")}
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Usuário
        </PermissionButton>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={
              isClientContext
                ? "Buscar por nome, documento, Unidade, matrícula, Cargo..."
                : "Buscar por nome, documento, matrícula, Cargo ou Departamento..."
            }
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-8"
          />
        </div>

        <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((current) => Math.max(0, current - LIMIT))}
            disabled={!canGoBack || usersQuery.isFetching}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((current) => current + LIMIT)}
            disabled={!hasNextPage || usersQuery.isFetching}
          >
            Próxima
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>

      {usersQuery.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">Carregando usuários...</Card>
      ) : usersQuery.isError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {errorMessage(usersQuery.error, "Erro ao carregar usuários")}
          </div>
          <Button variant="outline" className="mt-3" onClick={() => usersQuery.refetch()}>
            Tentar novamente
          </Button>
        </Card>
      ) : filteredUsers.length === 0 ? (
        <EmptyState title="Nenhum usuário encontrado" />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Documento</TableHead>
                {isClientContext ? <TableHead>Unidade</TableHead> : null}
                <TableHead>Matrícula</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Departamento</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[140px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {filteredUsers.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>{item.name}</span>
                      {item.email ? (
                        <span className="text-xs text-muted-foreground">{item.email}</span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{item.person_type ?? "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDocumentByType(item.document_number, item.person_type)}
                  </TableCell>
                  {isClientContext ? (
                    <TableCell className="text-sm text-muted-foreground">{item.unit_name ?? "-"}</TableCell>
                  ) : null}
                  <TableCell className="text-sm text-muted-foreground">{item.employee_code ?? "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{item.job_position_name ?? "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{item.department_name ?? "-"}</TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? "default" : "secondary"} className="text-xs">
                      {item.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <CrudRowActions
                      onView={() => navigate(`/cadastros/usuarios/${item.id}`)}
                      onEdit={() => navigate(`/cadastros/usuarios/${item.id}/editar`)}
                      onToggleActive={() => openStatusConfirmation(item)}
                      isActive={item.is_active}
                      disabled={isBusy}
                      viewPermission={readPermission}
                      editPermission={writePermission}
                      toggleActivePermission={writePermission}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={selected?.is_active ? "Desativar Usuário" : "Ativar Usuário"}
        description={
          selected?.is_active
            ? isClientContext
              ? "Deseja desativar o acesso deste usuário ao Cliente atual?"
              : "Deseja desativar este usuário na Empresa?"
            : "Deseja ativar este usuário novamente?"
        }
        onConfirm={confirmStatusChange}
        confirmLabel={selected?.is_active ? "Desativar" : "Ativar"}
        variant={selected?.is_active ? "destructive" : "default"}
      />
    </div>
  );
}
